import { FC, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  Link as LinkBase,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import { camelCaseToText, formatTime, getResponseSpeedColor } from "../utils";
import CustomTable from "components/Table/CustomTable";
import { OpenInNew, Info } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { FeedbackStatus, ProcessedFeedback } from "types/feedback";
import { FeedbackStatusChip } from "components/Feedback";
import { SubRequirementsModal } from "components/Feedback/SubRequirementsModal";
import {
  getFeedbackTypeLabel,
  getRequirementDisplayData,
} from "utils/feedbackIter";
import { Requirement } from "types/requirement";
import { UserWithPet } from "components/VetStore/CoachPetAvatar";

interface CoachesDetailProps {
  processedFeedback: ProcessedFeedback;
}

const getStatusChip = (status: string) => {
  if (status === FeedbackStatus.DONE) {
    return <Chip label="Done" color="success" variant="filled" />;
  }
  if (status === FeedbackStatus.DECLINED) {
    return <Chip label="Declined" color="error" variant="filled" />;
  }
  return <Chip label="Pending" color="warning" variant="filled" />;
};

export const CoachesDetail: FC<CoachesDetailProps> = ({
  processedFeedback,
}) => {
  const [openSubRequirementsModal, setOpenSubRequirementsModal] =
    useState(false);
  const [selectedIterData, setSelectedIterData] = useState<{
    iterName: string;
    subRequirements: Array<{
      requirement_id: string;
      name: string;
      description?: string | null;
    }>;
    ratingsByRequirement?: Record<
      string,
      {
        date: string;
        learnerRating?: number | null;
        coachRating?: number | null;
      }[]
    >;
  } | null>(null);
  const allRequirements: Requirement[] = []; // TODO: Fetch requirements if needed

  if (!processedFeedback) return null;

  const category = camelCaseToText(processedFeedback.category);

  const getResponseTime = (row: any) => {
    if (!row.reviewed_at || !row.created_at) return "-";
    const responseTimeInHours =
      (new Date(row.reviewed_at).getTime() -
        new Date(row.created_at).getTime()) /
      (1000 * 60 * 60);
    return formatTime(responseTimeInHours);
  };

  const getTypeChip = (feedback: any) => {
    const label = getFeedbackTypeLabel(feedback);
    if (!label) return null;
    const color =
      label === "Competencies" ? "success" : label === "ITER" ? "info" : "primary";
    return <Chip label={label} size="small" color={color} />;
  };

  return (
    <Box>
      <Stack spacing={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <UserWithPet
            pictureUrl={processedFeedback?.coach?.picture_url}
            name={processedFeedback?.coach?.name}
            equippedStoreItems={
              (processedFeedback?.coach as any)?.equipped_store_items
            }
            avatarSize={80}
          />
          <Box>
            <Typography variant="h6">
              {processedFeedback?.coach?.name}
            </Typography>
            <Chip
              label={category}
              sx={getResponseSpeedColor(category)}
              size="small"
            />
          </Box>
        </Stack>

        <Box>
          <Typography variant="body1" gutterBottom>
            <strong>Average response time:</strong>{" "}
            {formatTime(processedFeedback.avgResponseTime)}
          </Typography>
          <Typography variant="body1">
            <strong>Total feedback:</strong>{" "}
            {processedFeedback.feedbacks.length}
          </Typography>
        </Box>

        <Box sx={{ height: "calc(100vh - 20rem)", overflow: "scroll" }} pr={2}>
          <CustomTable
            entities={processedFeedback.feedbacks}
            fieldToFilter={(row, text) =>
              row.student.name.toLowerCase().includes(text.toLowerCase())
            }
            columns={[
              {
                id: "student",
                label: "Name",
                value: (row: any) => (
                  <Stack direction="row" spacing={2} alignItems="center">
                    <UserWithPet
                      pictureUrl={row?.student?.picture_url}
                      name={row?.student?.name}
                      equippedStoreItems={row?.student?.equipped_store_items}
                      avatarSize={40}
                    />
                    <Typography>{row?.student?.name}</Typography>
                  </Stack>
                ),
              },
              {
                id: "requirements",
                label: "Requirements",
                value: (row: any) => {
                  const requirementData = getRequirementDisplayData(row, allRequirements);
                  return (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2">{requirementData.name}</Typography>
                      {requirementData.isITER && requirementData.iterData && (
                        <Tooltip title="View Competencies">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Build rating history across all feedbacks for this coach & ITER
                              const history = processedFeedback.feedbacks
                                .filter((fb) => {
                                  const d = getRequirementDisplayData(
                                    fb as any,
                                    allRequirements,
                                  );
                                  return d.isITER && d.name === requirementData.name;
                                })
                                .sort((a, b) => {
                                  const da =
                                    a.observation_date || a.created_at || "";
                                  const db =
                                    b.observation_date || b.created_at || "";
                                  return da.localeCompare(db);
                                });

                              const ratingsByRequirement: Record<
                                string,
                                {
                                  date: string;
                                  learnerRating?: number | null;
                                  coachRating?: number | null;
                                }[]
                              > = {};

                              history.forEach((fb: any) => {
                                const date = (fb.observation_date ||
                                  fb.created_at) as string;
                                fb.feedback_skills.forEach((skill: any) => {
                                  const reqId =
                                    skill.requirement?.requirement_id;
                                  if (!reqId) return;
                                  if (!ratingsByRequirement[reqId]) {
                                    ratingsByRequirement[reqId] = [];
                                  }
                                  ratingsByRequirement[reqId].push({
                                    date,
                                    learnerRating: skill.self_rating ?? null,
                                    coachRating: skill.coach_rating ?? null,
                                  });
                                });
                              });

                              setSelectedIterData({
                                ...requirementData.iterData,
                                ratingsByRequirement,
                              });
                              setOpenSubRequirementsModal(true);
                            }}
                          >
                            <Info fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  );
                },
              },
              {
                id: "type",
                label: "Type",
                value: (row: any) => getTypeChip(row),
              },
              {
                id: "status",
                label: "Status",
                value: (row: any) => getStatusChip(row.status),
              },
              {
                id: "responseTime",
                label: "Response Time",
                value: (row: any) => {
                  return getResponseTime(row);
                },
              },
              {
                id: "openFeedback",
                value: (row: any) => (
                  <LinkBase
                    component={Link}
                    to={`/feedback/${row.feedback_id}`}
                    target="_blank"
                  >
                    <OpenInNew />
                  </LinkBase>
                ),
              },
            ]}
            isSelectable={false}
            idColumn="feedback_id"
            handleActionClick={() => {}}
            extraActionItems={[]}
            enableCardViewInSmallDevices={true}
            enableMenuInSmallDevices={false}
            renderCardContent={(row: any) => {
              return (
                <Stack spacing={0.5}>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
                    <UserWithPet
                      pictureUrl={row?.student?.picture_url}
                      name={row?.student?.name}
                      equippedStoreItems={row?.student?.equipped_store_items}
                      avatarSize={40}
                    />
                    <Typography>{row?.student?.name}</Typography>
                  </Stack>

                  <Typography variant="body1" fontWeight={600}>
                    Requirements:
                  </Typography>
                  {(() => {
                    const requirementData = getRequirementDisplayData(row, allRequirements);
                    return (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body1">{requirementData.name}</Typography>
                        {requirementData.isITER && requirementData.iterData && (
                          <Tooltip title="View Competencies">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedIterData(requirementData.iterData);
                                setOpenSubRequirementsModal(true);
                              }}
                            >
                              <Info fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    );
                  })()}

                  <Typography variant="body1">
                    <strong>Status:</strong>{" "}
                    <FeedbackStatusChip feedback={row} />
                  </Typography>

                  <Typography variant="body1">
                    <strong>Response Time:</strong> {getResponseTime(row)}
                  </Typography>
                </Stack>
              );
            }}
          />
        </Box>
      </Stack>

      {selectedIterData && (
        <SubRequirementsModal
          open={openSubRequirementsModal}
          onClose={() => {
            setOpenSubRequirementsModal(false);
            setSelectedIterData(null);
          }}
          iterName={selectedIterData.iterName}
          subRequirements={selectedIterData.subRequirements}
          ratingsByRequirement={selectedIterData.ratingsByRequirement}
        />
      )}
    </Box>
  );
};
